import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { Player } from '@loomhq/createhq/dist/video-player/api';
import {
  Video,
  VideoModel,
  useVideoContext,
} from '@loomhq/createhq/dist/video-player/context';
import { usePlayer } from '@loomhq/createhq/dist/video-player/hooks';
import React from 'react';
import { useSelector } from 'react-redux';

import { SEND_HLS_PLAYBACK_STATS } from 'constants/featureFlag';
import { DASH, M3U8, MP4, WEBM } from 'constants/mimes';
import { selectUserId } from 'selectors/user';
import { createDashInstance } from 'utilities/dash';
import { isFirefox, isIOS } from 'utilities/device';
import { createHlsInstance } from 'utilities/hls';
import { getHlsConfig } from 'utilities/hls-config';

import FetchCaptions from './FetchCaptions.gql';
import CheckPassword from './hooks/CheckPassword.gql';
import FetchVideoData from './hooks/FetchVideoData.gql';
import { usePasswordContext } from './password/context';
import {
  destroyMseTech,
  getPlaylistInfo,
  handleHlsJsBootUp,
  onDashError,
  setupDashMonitor,
  setupHlsMonitor,
  setupPlayerJsAdapter,
} from './utils';
import {
  MEDIA_ERR_DECODE,
  MEDIA_ERR_NETWORK,
  MEDIA_ERR_SRC_NOT_SUPPORTED,
} from '../../constants/video';

const CONFIG_HLS_JS_PLAYER_GQL = 'configHlsJsPlayer';

const CAPTIONS_QUERY = gql`
  ${FetchCaptions}
`;

type VideoSource = VideoModel['source'] & { plainSourceUrl?: string };

export function useHandleSource(video: Video, autoStartLoad = false): void {
  const player = usePlayer(video.id);
  const source = useVideoSource(String(video.modelId));
  const userAnonId: string = useSelector(selectUserId);

  React.useEffect(
    () => {
      if (!player || !source) {
        return;
      }

      // TODO: find a better place for this
      const isDash = source.sourceMimeType === DASH;
      const isHls = source.sourceMimeType === M3U8;
      const isMp4 = source.sourceMimeType === MP4;

      player.mime = source.sourceMimeType;

      const featureFlags = video?.videoFeatureFlags || {};
      const muxEnabled = featureFlags[SEND_HLS_PLAYBACK_STATS];

      const hlsServerConfigOverride =
        typeof featureFlags[CONFIG_HLS_JS_PLAYER_GQL] === 'object'
          ? featureFlags[CONFIG_HLS_JS_PLAYER_GQL]
          : {};

      if (isMp4) {
        player.media.src = source.sourceUrl;
      }

      if (isDash) {
        const beforeLoad = (videoElement: HTMLVideoElement) => {
          if (muxEnabled) {
            player.mux = setupDashMonitor(
              String(source.plainSourceUrl),
              videoElement,
              userAnonId,
              video
            );
          }
        };

        const onLoadError = (error: Error) => {
          // WARNING: these errors are not bubbled up!
          console.error('loadError', error);
          player.mux?.loadErrorHandler(error);
        };

        player.mseTech = createDashInstance({
          manifestUrl: source.sourceUrl,
          partCredentials: source.partCredentials,
          videoElement: player.media,
          beforeLoad,
          onLoadError,
        });

        player.mseTech.addEventListener(
          'error',
          onDashError({ videoId: video.id, videoType: source.sourceMimeType })
        );
      }

      if (isHls) {
        const hlsConfig = getHlsConfig(
          { autoStartLoad },
          hlsServerConfigOverride
        );

        if (isIOS) {
          player.media.src = source.sourceUrl;
        }

        player.mseTech = createHlsInstance(
          source.sourceUrl,
          player.media,
          hlsConfig
        );

        if (!autoStartLoad) {
          handleHlsJsBootUp(player);
        }

        if (muxEnabled) {
          setupHlsMonitor(
            String(source.plainSourceUrl),
            player,
            userAnonId,
            video
          );
        }
      }

      // setup embed API
      setupPlayerJsAdapter(player.media);

      return () => {
        destroyMseTech(player);
      };
    },
    // we DO not want video to be a dependency
    // captions will cause the effect to re-run
    // in a quick succession, breaking playback.
    // shut up eslinter!
    // eslint-disable-next-line
    [player, source, userAnonId]
  );
}

export function useVideoSource(
  videoId: string,
  forceOriginal = false,
  forceMp4 = false
): VideoSource {
  const [source, setSource] = React.useState<any>(null);
  const { password } = usePasswordContext();

  React.useEffect(() => {
    getPlaylistInfo(videoId, String(password), forceOriginal, forceMp4).then(
      setSource
    );
  }, [forceMp4, forceOriginal, password, videoId]);

  // TODO: should listen to a fallback event on the player
  // and refetch the mp4 version, returning a new source and
  // re-triggering the useSource hook?

  return source;
}

export function useCaptionsUrlQuery(videoId?: string): void {
  const { setCaptionsUrl } = useVideoContext();
  const { data } = useQuery(CAPTIONS_QUERY, {
    variables: { videoId },
  });

  React.useEffect(() => {
    setCaptionsUrl(data?.fetchVideoTranscript?.captions_source_url);
  }, [data, setCaptionsUrl]);
}

const MAX_DECODE_ERROR_RETRIES = 5;
const MAX_SRC_ERROR_RETRIES = 3;

const retryVideoPlaybackSrcError = (errorCode, player: Player) => {
  // eslint-disable-next-line
  const retriesRef = React.useRef(0);

  if (retriesRef.current >= MAX_SRC_ERROR_RETRIES) {
    if (![WEBM, M3U8, DASH].includes(player.mime)) {
      return console.error('media src error', errorCode);
    }

    // try MP4
    loadFallbackSource(player);
  }

  // sometimes the media is not ready in Cloudfront even though it's
  // ready in S3, so we catch that network error here and retry
  // https://github.com/videojs/video.js/issues/1805#issuecomment-273671931
  //
  // TODO: check if this is still true? instrument
  // TODO: move to player api
  window.setTimeout(() => {
    const currentTime = player.currentTime;
    const video = player.media;

    retriesRef.current++;

    video.pause();
    video.load();
    player.currentTime = currentTime;
    player.safePlay();
  }, 100 * Math.pow(2, retriesRef.current));
};

const retryVideoPlaybackDecodeError = (player: Player, videoId: string) => {
  // eslint-disable-next-line
  const retriesRef = React.useRef(0);

  if (retriesRef.current >= MAX_DECODE_ERROR_RETRIES) {
    return console.error(Error('Embed Video Decode Error'), {
      error_code: MEDIA_ERR_DECODE,
      video_id: videoId,
      video_type: player.mime,
    });
  }

  // TODO, it should resume from currentTime

  retriesRef.current++;

  loadFallbackSource(player);
};

const loadFallbackSource = (player: Player) => {
  destroyMseTech(player);

  // TODO: fetch transcoded url and restart the player
  // const restartPlayback = () => {
  //   player.pause();
  //   player.media.load();
  //   player.safePlay(); // it may fail if autoplay is blocked by the browser
  // };
};

const _onError = (player: Player, videoId: string) => {
  const isDash = player.mime === DASH;
  const { error } = player.media;
  const { code } = error || {}; // sometimes `error` is `null`

  // This error should be ignored in Firefox when playing v5 sources (DASH)
  if (code === MEDIA_ERR_SRC_NOT_SUPPORTED && isFirefox && isDash) {
    return;
  }

  console.error(error);

  if (
    code !== undefined &&
    [MEDIA_ERR_NETWORK, MEDIA_ERR_SRC_NOT_SUPPORTED].includes(code)
  ) {
    retryVideoPlaybackSrcError(code, player);
  }

  if (code === MEDIA_ERR_DECODE) {
    retryVideoPlaybackDecodeError(player, videoId);
  }
};

export function useFetchVideoData(videoId: string): any {
  const { loading, error, data } = useQuery(FetchVideoData, {
    variables: { id: videoId },
  });

  return { loading, error, data };
}

export function useCheckPassword(): any {
  const [checkPassword, { loading, error, data }] = useLazyQuery(
    CheckPassword,
    {
      fetchPolicy: 'no-cache',
    }
  );

  return [checkPassword, { loading, error, data }];
}
